import React, { FC } from 'react';
import { GetLatestReleases_getLatestReleases_items } from '../__queries__';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useI18n } from '~Hooks';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import ReleaseImage from '../ReleaseImage';

const Wrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  text-transform: none;
  position: relative;
  padding: 8px 16px 8px 16px;
  border-radius: 0px;
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const NotesList = styled.ul`
  list-style-type: disc;
  margin: 0;
  padding-left: 20px;
  color: var(--colorWhite);
`;

const NoteItem = styled.li`
  margin-bottom: 4px;
`;

interface Props {
  release: GetLatestReleases_getLatestReleases_items;
}

/** UI Component for release with type RELEASE_NOTES */
const ReleaseNotes: FC<Props> = ({ release }) => {
  const { i18n } = useI18n();

  // Collect all images from all notes
  const allImages = release.notes
    .filter((note) => note.imageUrls && note.imageUrls.length > 0)
    .flatMap((note) => note.imageUrls || []);

  if (release.notes.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Box
        display="flex"
        gap="small"
        flexDirection="row"
        alignItems="flex-start"
        style={{ position: 'relative', zIndex: 1 }}>
        <ReleaseImage imageUrls={allImages} />

        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Paragraph level={5} color="var(--colorBlueGray300)" top={4} bottom={4}>
            {DateTime.fromISO(release.createdAt, { zone: 'utc' })
              .setZone('local')
              .toFormat('d MMMM yyyy HH:mm')}
          </Paragraph>

          <Heading level={4} bottom={0} color="var(--colorWhite)">
            {i18n(release.titleLang)}
          </Heading>

          <NotesList>
            {release.notes.map((note, index) => (
              <NoteItem key={note.titleLang.en + index}>
                <Paragraph level={5} fontWeight={600} color="var(--colorWhite)" bottom={0}>
                  {i18n(note.titleLang)}
                </Paragraph>
              </NoteItem>
            ))}
          </NotesList>
        </Box>
      </Box>
    </Wrapper>
  );
};

export default ReleaseNotes;
